// W06 / M06 — Feladat részletei.
//
// Desktopon a lista melletti panelben jelenik meg (nem modálisan), mobilon a
// lista helyét veszi át. A műveletek innen nyílnak: státuszváltás, elfogadás,
// időmérés felülírása.

import { t } from '../lib/i18n';
import { avatar, avatarStack, priorityTag, statusBadge } from '../lib/components';
import { h, icon, icons } from '../lib/dom';
import type { PersonView } from '../lib/data';
import { allowedAttachmentTypes, checkAttachment } from './task-forms';
import {
  dateTimeField,
  numberField,
  textField,
  validateAll,
  type FieldControl,
} from '../lib/fields';
import {
  formatClock,
  formatDeadline,
  formatDeadlineShort,
  formatElapsed,
  type TaskAttachment,
  type TaskComment,
  type TaskView,
} from '../lib/model';

export type TaskActions = {
  onChangeStatus: (task: TaskView) => void;
  onAcceptDecline: (task: TaskView) => void;
  onOverrideTime: (task: TaskView) => void;
  /// Az időmérés indítása és leállítása.
  ///
  /// Külön a felülírástól: az „Időmérés" gomb eddig KIZÁRÓLAG az utólagos
  /// felülírást nyitotta, indítani a webről egyáltalán nem lehetett — a
  /// mobilon viszont igen (`tasks_repo.timerTask`).
  onStartTimer: (task: TaskView) => void;
  onStopTimer: (task: TaskView) => void;
  onReopen: (task: TaskView) => void;
  /// A terv (W12) a fülön belül, a lista alatt írja meg a megjegyzést —
  /// nem külön párbeszédablakban. A `payload` hiánya a régi utat jelenti
  /// (párbeszédablak megnyitása), ezért marad opcionálisnak.
  onComment: (task: TaskView, payload?: { text: string; mentionedIds: string[] }) => void;
  onAttach: (task: TaskView) => void;
  onSign: (task: TaskView) => void;
  /// Egy fájl feltöltése. A visszaadott ígéret akkor teljesül, amikor a
  /// csatolmány már a feladaton is ott van.
  onUpload: (task: TaskView, file: File, onProgress: (ratio: number) => void) => Promise<void>;
  onRemoveAttachment: (task: TaskView, url: string) => void;
  onEditFields: (task: TaskView) => void;
  /// A W15 fül saját űrlapjáról érkező, kitöltött mezőértékek.
  onSaveFields: (task: TaskView, values: Record<string, string>) => void;
  onClose?: () => void;
};

type DetailTab = 'timeline' | 'comments' | 'attachments' | 'fields';

/// A nyitott fül a részletek ÚJRARAJZOLÁSÁT is túléli.
///
/// A listáról minden kattintás új `taskDetail()`-t épít; ha a fül a függvényen
/// belül élne, egy megjegyzés mentése után a felhasználó visszakerülne az
/// idővonalra, épp arról a fülről, amin dolgozik.
let activeTab: DetailTab = 'timeline';

function nameOf(people: PersonView[], id: string): string {
  return people.find((p) => p.id === id)?.fullName ?? 'Ismeretlen';
}

/// Egy adatsor a részletek kártyájában: 140 px-es címke, mellette az érték.
///
/// A terv fekvő elrendezést mutat; korábban kétoszlopos rácsban, címke FÖLÖTT
/// értékkel álltak, ami ugyanannyi adatot kétszer akkora helyen mutatott.
function detailRow(label: string, value: HTMLElement | string): HTMLElement {
  return h(
    'div',
    {
      class: 'row g12',
      style: { alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--border)' },
    },
    h('span', { class: 'caption', style: { width: '140px', flex: 'none' }, text: label }),
    typeof value === 'string'
      ? h('span', {
          class: 'body-sm',
          style: { flexGrow: '1', minWidth: '0', overflowWrap: 'anywhere' },
          text: value,
        })
      : h(
          'span',
          { class: 'row g6', style: { flexGrow: '1', minWidth: '0', flexWrap: 'wrap' } },
          value,
        ),
  );
}

/// Dátum + idő rövid alakban az idővonalhoz és a megjegyzésekhez.
///
/// Hiányzó dátumnál `undefined`, nem gondolatjel: a hívó összefűzi a névvel,
/// és a „Tóth Anita · —" alak azt sugallná, hogy van itt valami, csak nem
/// tudjuk elolvasni.
function stamp(date?: Date): string | undefined {
  return date ? formatDeadlineShort(date) : undefined;
}

/// A tervben a fejléc jobb szélén álló „…" gomb.
///
/// A ritkábban kellő műveletek ide kerülnek, hogy a fejlécben ne álljon
/// egymás mellett nyolc egyenrangú gomb — korábban két teli sornyi volt
/// belőlük, és a státuszváltás ugyanakkora súlyt kapott, mint a „Mezők".
function overflowMenu(items: { label: string; onSelect: () => void }[]): HTMLElement {
  const list = h('div', {
    class: 'panel col',
    style: {
      position: 'absolute',
      top: 'calc(100% + 6px)',
      right: '0',
      minWidth: '200px',
      padding: '6px',
      zIndex: '20',
      display: 'none',
      boxShadow: 'var(--e3)',
    },
  });

  function setOpen(open: boolean): void {
    list.style.display = open ? 'flex' : 'none';
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      document.addEventListener('click', onOutside, true);
      document.addEventListener('keydown', onEscape, true);
    } else {
      document.removeEventListener('click', onOutside, true);
      document.removeEventListener('keydown', onEscape, true);
    }
  }

  function onOutside(event: MouseEvent): void {
    if (!wrap.contains(event.target as Node)) setOpen(false);
  }

  function onEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      setOpen(false);
      button.focus();
    }
  }

  const button = h(
    'button',
    {
      class: 'btn btn-ghost btn-icon',
      type: 'button',
      'aria-label': t('task.moreActions'),
      'aria-haspopup': 'true',
      'aria-expanded': 'false',
      onClick: () => setOpen(list.style.display === 'none'),
    },
    h('span', { class: 'ic' }, icon(icons.more)),
  );

  for (const item of items) {
    list.append(
      h('button', {
        class: 'btn btn-ghost btn-sm',
        type: 'button',
        style: { justifyContent: 'flex-start', width: '100%' },
        text: item.label,
        onClick: () => {
          setOpen(false);
          item.onSelect();
        },
      }),
    );
  }

  const wrap = h('div', { style: { position: 'relative' } }, button, list);
  return wrap;
}

type TimelineEntry = {
  title: string;
  cls: string;
  at?: Date;
  who?: string;
  note?: string;
};

/// Az idővonal a TÉNYLEGESEN tárolt időpontokból.
///
/// A modellben nincs státuszváltás-napló (ki, mikor, mire váltott): csak a
/// létrehozás, az első indítás, a lezárás és az újranyitások időpontja van
/// eltárolva. A terv teljes, lépésenkénti naplót mutat — azt kitalálni nem
/// lehet, így csak az áll itt, ami valóban meg van.
function timelineOf(task: TaskView, people: PersonView[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  const assigner = task.submittedUserId ? nameOf(people, task.submittedUserId) : undefined;

  for (const reopen of task.reopens) {
    entries.push({
      title: t('task.reopened'),
      cls: 'st-wait',
      at: reopen.at,
      note: reopen.reason,
    });
  }

  if (task.finishedAt) {
    entries.push({
      title: task.status === 'failed' ? t('task.unsuccessfulFulfillment') : t('task.closed'),
      cls: task.status === 'failed' ? 'st-fail' : 'st-ok',
      at: task.finishedAt,
      note: task.failedDesc ?? task.statusChangeDesc,
    });
  }

  if (task.startedAt) {
    entries.push({ title: t('task.startedShort'), cls: 'st-prog', at: task.startedAt });
  }

  if (task.status === 'unaccepted' && task.declineDesc) {
    entries.push({ title: t('task.declined'), cls: 'st-rej', note: task.declineDesc });
  }

  entries.push({
    title: t('task.createdShort'),
    cls: 'st-todo',
    at: task.createdAt,
    who: assigner,
    note: task.projectTitle ? `Projekt: ${task.projectTitle}` : undefined,
  });

  return entries;
}

function timelineTab(task: TaskView, people: PersonView[]): HTMLElement {
  const entries = timelineOf(task, people);

  return h(
    'div',
    { class: 'card pad16 col', style: { gap: '14px' } },
    ...entries.map((entry) =>
      h(
        'div',
        { class: 'row g12', style: { alignItems: 'flex-start' } },
        h(
          'span',
          {
            class: `badge ${entry.cls}`,
            style: {
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              padding: '0',
              flex: 'none',
              justifyContent: 'center',
            },
          },
          h('span', { class: 'ic', style: { width: '14px', height: '14px' } }, icon(icons.dot, 14)),
        ),
        h(
          'div',
          { class: 'col', style: { gap: '2px', minWidth: '0' } },
          h('span', { class: 'body-sm w6', text: entry.title }),
          h('span', {
            class: 'caption',
            text: [entry.who, stamp(entry.at)].filter(Boolean).join(' · ') || t('task.noTimestamp'),
          }),
          entry.note
            ? h('span', { class: 'body-sm ts', style: { marginTop: '2px' }, text: entry.note })
            : null,
        ),
      ),
    ),
  );
}

/// Egy megjegyzés a terv szerint: avatar, név, időpont, majd a szöveg
/// `.sunken` buborékban. A megjelölt nevek (`@Valaki`) akcentusszínnel.
function commentRow(comment: TaskComment, people: PersonView[]): HTMLElement {
  const author = nameOf(people, comment.authorId);

  /// A `@Név` darabokat külön elemekre bontjuk, hogy kiemelhessük őket.
  ///
  /// A szöveget SOHA nem HTML-ként illesztjük be: a megjegyzés felhasználói
  /// tartalom, egy `<img onerror=…>` ugyanúgy lefutna, mint bármi más.
  function withMentions(text: string): (HTMLElement | string)[] {
    const names = people
      .map((p) => p.fullName)
      .filter((n) => n.length > 0)
      .sort((a, b) => b.length - a.length);

    const parts: (HTMLElement | string)[] = [];
    let rest = text;

    while (rest.length > 0) {
      const at = rest.indexOf('@');
      if (at < 0) {
        parts.push(rest);
        break;
      }
      if (at > 0) parts.push(rest.slice(0, at));

      const after = rest.slice(at + 1);
      const hit = names.find((n) => after.startsWith(n));
      if (hit) {
        parts.push(
          h('span', { class: 'w6', style: { color: 'var(--accent)' }, text: `@${hit}` }),
        );
        rest = after.slice(hit.length);
      } else {
        parts.push('@');
        rest = after;
      }
    }

    return parts;
  }

  return h(
    'div',
    { class: 'row g10', style: { alignItems: 'flex-start' } },
    avatar(author),
    h(
      'div',
      { class: 'col', style: { gap: '4px', minWidth: '0', flexGrow: '1' } },
      h(
        'div',
        { class: 'row g8', style: { alignItems: 'baseline' } },
        h('span', { class: 'body-sm w6', text: author }),
        h('span', { class: 'caption', text: stamp(comment.createdAt) ?? '' }),
      ),
      h(
        'div',
        { class: 'sunken', style: { padding: '10px 12px', borderRadius: 'var(--r-sm)' } },
        h(
          'span',
          { class: 'body-sm', style: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } },
          ...withMentions(comment.text),
        ),
        comment.attachments.length > 0
          ? h(
              'div',
              { class: 'row g6', style: { marginTop: '6px', flexWrap: 'wrap' } },
              ...comment.attachments.map((item) =>
                item.url
                  ? h(
                      'a',
                      {
                        href: item.url,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        'aria-label': t('task.openAttachment'),
                        style: { display: 'inline-flex' },
                      },
                      item.kind === 'photo'
                        ? h('img', {
                            src: item.url,
                            alt: '',
                            loading: 'lazy',
                            style: {
                              width: '52px',
                              height: '52px',
                              borderRadius: '6px',
                              objectFit: 'cover',
                              border: '1px solid var(--border)',
                            },
                          })
                        : h(
                            'span',
                            {
                              class: 'ic',
                              style: {
                                width: '52px',
                                height: '52px',
                                borderRadius: '6px',
                                background: 'var(--border)',
                                color: 'var(--tm)',
                              },
                            },
                            icon(icons.clip, 18),
                          ),
                    )
                  : null,
              ),
            )
          : null,
      ),
    ),
  );
}

/// A terv szerinti szerkesztő a lista ALATT: szövegmező, `@` beírásra
/// felugró névlista, számláló és Küldés.
///
/// Eddig a megjegyzés külön párbeszédablakban készült, és a megjelölést az
/// összes ember chipsorából kellett kikattintani — a terv szerint a `@`
/// beírása szűri a listát, és a szövegbe is beleírja a nevet.
function commentComposer(
  task: TaskView,
  people: PersonView[],
  actions: TaskActions,
): HTMLElement {
  const textarea = h('textarea', {
    class: 'input ta',
    style: { minHeight: '76px' },
    maxlength: '2000',
    'aria-label': t('task.newComment'),
    placeholder: t('task.commentPlaceholderMention'),
  }) as HTMLTextAreaElement;

  const counter = h('span', { class: 'counter', text: '0 / 2000' });
  const error = h('span', { class: 'errtx', style: { display: 'none' } });

  const menu = h('div', {
    class: 'card el2',
    style: {
      position: 'absolute',
      left: '0',
      bottom: '100%',
      marginBottom: '6px',
      width: '260px',
      padding: '6px',
      display: 'none',
      zIndex: '10',
      maxHeight: '220px',
      overflow: 'auto',
    },
  });

  /// A kurzor elotti, befejezetlen `@…` darab.
  function pendingMention(): { start: number; query: string } | null {
    const upto = textarea.value.slice(0, textarea.selectionStart);
    const at = upto.lastIndexOf('@');
    if (at < 0) return null;
    const query = upto.slice(at + 1);
    // Ujsor utan mar nem ugyanaz a megjeloles; a nevekben viszont van szokoz,
    // ezert a szokoz onmagaban nem zarja le.
    if (query.includes('\n')) return null;
    return { start: at, query };
  }

  function insert(name: string, start: number): void {
    const before = textarea.value.slice(0, start);
    const after = textarea.value.slice(textarea.selectionStart);
    textarea.value = `${before}@${name} ${after}`;
    const caret = before.length + name.length + 2;
    textarea.setSelectionRange(caret, caret);
    textarea.focus();
    menu.style.display = 'none';
    counter.textContent = `${textarea.value.length} / 2000`;
  }

  function drawMenu(): void {
    const pending = pendingMention();
    if (!pending) {
      menu.style.display = 'none';
      return;
    }

    const query = pending.query.toLocaleLowerCase('hu');
    const matches = people
      .filter((p) => p.fullName.toLocaleLowerCase('hu').includes(query))
      .slice(0, 6);

    if (matches.length === 0) {
      menu.style.display = 'none';
      return;
    }

    menu.replaceChildren(
      h('span', { class: 'caption', style: { padding: '4px 8px', display: 'block' }, text: t('task.mention') }),
      ...matches.map((person) =>
        h(
          'button',
          {
            type: 'button',
            class: 'navitem',
            style: { height: '38px', width: '100%', border: '0', font: 'inherit', cursor: 'pointer' },
            onClick: () => insert(person.fullName, pending.start),
          },
          avatar(person.fullName, 'sm', person.photoUrl),
          h(
            'span',
            { class: 'col', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
            h('span', { class: 'body-sm w6', text: person.fullName }),
            person.jobTitle ? h('span', { class: 'caption', text: person.jobTitle }) : null,
          ),
        ),
      ),
    );
    menu.style.display = 'flex';
  }

  textarea.addEventListener('input', () => {
    counter.textContent = `${textarea.value.length} / 2000`;
    if (textarea.value.trim().length > 0) error.style.display = 'none';
    drawMenu();
  });
  textarea.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && menu.style.display !== 'none') {
      event.stopPropagation();
      menu.style.display = 'none';
    }
  });

  function send(): void {
    const text = textarea.value.trim();
    if (text.length === 0) {
      error.textContent = t('task.needCommentText');
      error.style.display = '';
      textarea.focus();
      return;
    }
    // A megjelolteket a szovegbol olvassuk vissza: igy az is bekerul, akit a
    // felhasznalo kezzel gepelt be, nem a listabol valasztott.
    const mentionedIds = people
      .filter((p) => p.fullName.length > 0 && text.includes(`@${p.fullName}`))
      .map((p) => p.id);

    actions.onComment(task, { text, mentionedIds });
    textarea.value = '';
    counter.textContent = '0 / 2000';
  }

  return h(
    'div',
    {
      class: 'col g8',
      style: { padding: '12px', borderTop: '1px solid var(--border)', background: 'var(--surface)' },
    },
    h('div', { style: { position: 'relative' } }, menu, textarea),
    error,
    h(
      'div',
      { class: 'row g8', style: { alignItems: 'center' } },
      h(
        'button',
        {
          class: 'btn btn-ghost btn-icon',
          type: 'button',
          'aria-label': t('task.addAttachment'),
          onClick: () => actions.onAttach(task),
        },
        h('span', { class: 'ic' }, icon(icons.clip, 18)),
      ),
      counter,
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('task.send'),
        style: { marginLeft: 'auto' },
        onClick: send,
      }),
    ),
  );
}

function commentsTab(
  task: TaskView,
  people: PersonView[],
  actions: TaskActions,
): HTMLElement {
  return h(
    'div',
    { class: 'panel col', style: { overflow: 'hidden' } },
    h(
      'div',
      { class: 'col g16', style: { padding: '18px' } },
      ...(task.comments.length === 0
        ? [h('span', { class: 'body-sm tm', text: t('task.noCommentOnTask') })]
        : task.comments.map((comment) => commentRow(comment, people))),
    ),
    commentComposer(task, people, actions),
  );
}

/// A csatolmány neve a Storage-hivatkozásból.
///
/// A modell a fájlnevet NEM tárolja (`AttachmentModel.fileName` se a
/// mentésben, se a betöltésben nem szerepel), így a Storage-útvonal utolsó
/// darabja az egyetlen valódi forrás. A feltöltéskor elé tett időbélyeget
/// levágjuk, mert az nem a felhasználó nevéből jött.
export function attachmentName(url?: string): string {
  if (!url) return '';
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const last = path.split('/').pop() ?? '';
    const name = last.split('?')[0] ?? '';
    return name.replace(/^\d{10,}_/, '');
  } catch {
    return '';
  }
}

function imageTile(item: TaskAttachment, onRemove?: () => void): HTMLElement {
  const name = attachmentName(item.url);
  return h(
    'div',
    { class: 'col', style: { gap: '5px', minWidth: '0' } },
    h(
      'div',
      {
        style: {
          height: '104px',
          borderRadius: '10px',
          background: 'var(--sunken)',
          border: '1px solid var(--border)',
          position: 'relative',
          overflow: 'hidden',
        },
      },
      item.url
        ? h('a', { href: item.url, target: '_blank', rel: 'noopener noreferrer' },
            h('img', {
              src: item.url,
              alt: name || t('task.attachedImage'),
              loading: 'lazy',
              style: { width: '100%', height: '104px', objectFit: 'cover', display: 'block' },
            }),
          )
        : null,
      onRemove
        ? h(
            'button',
            {
              class: 'btn btn-ghost btn-icon btn-sm',
              type: 'button',
              'aria-label': t('task.removeNamed', [name || t('task.image')]),
              style: {
                position: 'absolute',
                top: '4px',
                right: '4px',
                background: 'var(--surface)',
                boxShadow: 'var(--e1)',
              },
              onClick: onRemove,
            },
            h('span', { class: 'ic', style: { width: '13px', height: '13px' } }, icon(icons.close, 13)),
          )
        : null,
    ),
    h('span', {
      class: 'caption',
      style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      title: name,
      text: name || t('task.image'),
    }),
  );
}

function fileRow(item: TaskAttachment, onRemove?: () => void): HTMLElement {
  const name = attachmentName(item.url);
  return h(
    'div',
    {
      class: 'row g10',
      style: { alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border)' },
    },
    h(
      'span',
      {
        class: 'ic',
        style: {
          width: '34px',
          height: '34px',
          borderRadius: '8px',
          background: 'var(--sunken)',
          color: 'var(--ts)',
          flex: 'none',
        },
      },
      icon(icons.clip, 17),
    ),
    h(
      'span',
      { class: 'col grow', style: { gap: '0', minWidth: '0' } },
      h('span', {
        class: 'body-sm w6',
        style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        text: name || t('task.file'),
      }),
      // A terv itt meretet es feltoltesi idot is mutat. Egyiket sem taroljuk
      // (`AttachmentModel.size` es `fileName` nem kerul a dokumentumba), ezert
      // az marad, ami TENYLEG tudhato: honnan szarmazik a csatolmany.
      h('span', {
        class: 'caption',
        text:
        item.source === 'assigned' ? t('task.attachedOnAssign') : t('task.attachedOnFinish'),
      }),
    ),
    item.url
      ? h(
          'a',
          {
            class: 'btn btn-ghost btn-icon btn-sm',
            href: item.url,
            target: '_blank',
            rel: 'noopener noreferrer',
            'aria-label': t('task.openNamed', [name || t('task.file')]),
          },
          h('span', { class: 'ic' }, icon(icons.download)),
        )
      : null,
    onRemove
      ? h(
          'button',
          {
            class: 'btn btn-ghost btn-icon btn-sm',
            type: 'button',
            'aria-label': t('task.deleteNamed', [name || t('task.file')]),
            onClick: onRemove,
          },
          h('span', { class: 'ic' }, icon(icons.trash)),
        )
      : null,
  );
}

/// Feltöltés alatt álló fájl sora, a terv folyamatsávjával.
function uploadingRow(name: string, ratio: number): HTMLElement {
  const percent = Math.round(ratio * 100);
  return h(
    'div',
    {
      class: 'row g10',
      style: { alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border)' },
    },
    h(
      'span',
      {
        class: 'ic',
        style: {
          width: '34px',
          height: '34px',
          borderRadius: '8px',
          background: 'var(--sunken)',
          color: 'var(--ts)',
          flex: 'none',
        },
      },
      icon(icons.clip, 17),
    ),
    h(
      'span',
      { class: 'col grow', style: { gap: '0', minWidth: '0' } },
      h('span', {
        class: 'body-sm w6',
        style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        text: name,
      }),
    ),
    h(
      'div',
      { class: 'col', style: { width: '120px', gap: '4px', flex: 'none' } },
      h(
        'div',
        {
          class: 'prog',
          role: 'progressbar',
          'aria-valuenow': String(percent),
          'aria-valuemin': '0',
          'aria-valuemax': '100',
          'aria-label': `${name} feltöltése`,
        },
        h('i', { style: { width: `${percent}%` } }),
      ),
      h('span', { class: 'caption mono', style: { textAlign: 'right' }, text: `${percent}%` }),
    ),
  );
}

function attachmentsTab(task: TaskView, actions: TaskActions): HTMLElement {
  const images = task.attachments.filter((a) => a.kind === 'photo');
  const files = task.attachments.filter((a) => a.kind === 'file');

  const uploads = h('div');
  const problems = h('div', { class: 'col g6' });
  const host = h('div', { class: 'col g16' });

  /// A folyamatban levo feltoltesek allapota. Csak ez valtozik menet kozben,
  /// ezert a tobbi reszt nem rajzoljuk ujra.
  const running = new Map<string, number>();

  function drawUploads(): void {
    uploads.replaceChildren(
      ...[...running.entries()].map(([name, ratio]) => uploadingRow(name, ratio)),
    );
  }

  function reject(message: string): void {
    problems.append(
      h(
        'div',
        {
          class: 'row g10',
          style: {
            alignItems: 'center',
            padding: '10px 12px',
            borderRadius: '10px',
            background: 'var(--err-soft)',
          },
        },
        h('span', { class: 'ic', style: { color: 'var(--err)', flex: 'none' } }, icon(icons.warning)),
        h('span', { class: 'body-sm', style: { color: 'var(--err)' }, text: message }),
      ),
    );
  }

  function accept(list: FileList | File[] | null): void {
    problems.replaceChildren();
    for (const file of [...(list ?? [])]) {
      const check = checkAttachment(file);
      if (!check.ok) {
        reject(`${file.name} — ${check.message}`);
        continue;
      }
      running.set(file.name, 0);
      drawUploads();
      actions.onUpload(task, file, (ratio) => {
        running.set(file.name, ratio);
        drawUploads();
      })
        .catch((error: unknown) => {
          reject(
            `${file.name} — ${
              error instanceof Error ? error.message : t('task.uploadAborted')
            }`,
          );
        })
        .finally(() => {
          running.delete(file.name);
          drawUploads();
        });
    }
  }

  const picker = h('input', {
    type: 'file',
    multiple: 'multiple',
    accept: allowedAttachmentTypes.join(','),
    style: { display: 'none' },
    onChange: (event: Event) => {
      const input = event.target as HTMLInputElement;
      accept(input.files);
      // Ugyanaz a fajl ujra kivalaszthato legyen.
      input.value = '';
    },
  }) as HTMLInputElement;

  const drop = h(
    'div',
    {
      class: 'drop col',
      style: { alignItems: 'center', gap: '8px' },
      onDragOver: (event: DragEvent) => {
        event.preventDefault();
        drop.classList.add('over');
      },
      onDragLeave: () => drop.classList.remove('over'),
      onDrop: (event: DragEvent) => {
        event.preventDefault();
        drop.classList.remove('over');
        accept(event.dataTransfer?.files ?? null);
      },
    },
    h('span', { class: 'ic', style: { width: '22px', height: '22px' } }, icon(icons.upload, 22)),
    h('span', { class: 'body w6', text: t('task.dropFiles') }),
    h(
      'span',
      { class: 'caption' },
      'vagy ',
      h('button', {
        class: 'btn btn-ghost btn-sm',
        type: 'button',
        text: t('task.browse'),
        onClick: () => picker.click(),
      }),
      t('task.dropHint'),
    ),
    picker,
  );

  host.replaceChildren(
    drop,
    problems,
    uploads,
    h(
      'div',
      { class: 'col g6' },
      h('span', { class: 'h3', text: t('task.images') }),
      images.length === 0
        ? h('span', { class: 'body-sm tm', text: t('task.noImagesOnTask') })
        : h(
            'div',
            {
              style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '10px',
              },
            },
            ...images.map((item) =>
              imageTile(item, item.url ? () => actions.onRemoveAttachment(task, item.url!) : undefined),
            ),
          ),
    ),
    h(
      'div',
      { class: 'col g6' },
      h('span', { class: 'h3', text: t('task.files') }),
      files.length === 0
        ? h('span', { class: 'body-sm tm', text: t('task.noFilesOnTask') })
        : h(
            'div',
            { class: 'panel', style: { overflow: 'hidden' } },
            ...files.map((item) =>
              fileRow(item, item.url ? () => actions.onRemoveAttachment(task, item.url!) : undefined),
            ),
          ),
    ),
    // Az alairas kulon szakasz: nem a felhasznalo toltotte fel, es nem is
    // torolheto innen — a leigazolas resze.
    task.signatureUrl
      ? h(
          'div',
          { class: 'col g6' },
          h('span', { class: 'h3', text: t('task.signatureLabel') }),
          h('img', {
            src: task.signatureUrl,
            alt: t('task.signatureOnCloseAlt'),
            style: {
              maxWidth: '280px',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--border)',
            },
          }),
        )
      : h('button', {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          style: { alignSelf: 'flex-start' },
          text: t('task.captureSignature'),
          onClick: () => actions.onSign(task),
        }),
  );

  return host;
}

/// W15 — Dinamikus mezők.
///
/// A terv szerkeszthető űrlapot mutat kétoszlopos rácsban, nem listát egy
/// „Mezők kitöltése" gombbal. A mezők itt, helyben tölthetők ki.
function fieldsTab(task: TaskView, actions: TaskActions): HTMLElement {
  // A tipusmezo nem kitoltendo ertek, azt a fejlec mutatja.
  const editable = task.fields.filter((field) => !field.isType);
  const typeField = task.fields.find((field) => field.isType);

  const host = h('div', { class: 'col g16' });
  if (editable.length === 0) {
    host.replaceChildren(
      h(
        'div',
        { class: 'panel pad24 col g12' },
        h('span', { class: 'body-sm tm', text: t('task.noCustomFieldOnTask') }),
        h('span', {
          class: 'caption',
          text:
            t('task.fieldsFromTemplate'),
        }),
      ),
    );
    return host;
  }

  /// A helyi piszkozat kulcsa. Csak ezen a gépen, ezen a böngészőben él —
  /// nem adat, hanem kényelem: egy félbehagyott kitöltés ne vesszen el, ha
  /// valaki közben átkattint egy másik feladatra.
  const draftKey = `blaze.fields.${task.id}`;

  let draft: Record<string, string> = {};
  try {
    draft = JSON.parse(localStorage.getItem(draftKey) ?? '{}') as Record<string, string>;
  } catch {
    draft = {};
  }

  const values = new Map<string, string>();
  for (const field of editable) {
    values.set(field.id ?? field.name, draft[field.id ?? field.name] ?? field.value ?? '');
  }

  const savedNote = h('span', { class: 'pending', style: { display: 'none' } });
  const missing = h('span', { class: 'caption' });

  function requiredMissing(): number {
    // A `status` mezo azt mondja meg, MELYIK statuszba lepeshez kell kitolteni.
    // Az a mezo szamit kotelezonek, amihez tartozik ilyen felteteel.
    return editable.filter(
      (field) => field.requiredInStatus !== undefined && !values.get(field.id ?? field.name),
    ).length;
  }

  function updateHeader(): void {
    const count = requiredMissing();
    missing.textContent = `${editable.length} mező${
      count > 0 ? ` · ${count} kötelező kitöltetlen` : ''
    }`;
  }

  function rememberDraft(): void {
    try {
      localStorage.setItem(draftKey, JSON.stringify(Object.fromEntries(values)));
      savedNote.textContent = `Helyben mentve ${formatClock(new Date())}-kor`;
      savedNote.style.display = '';
    } catch {
      // Privat ablakban vagy tiltott tarolasnal nincs piszkozat — a kitoltes
      // ettol meg mukodik, csak nem eli tul a lapvaltast.
    }
  }

  const controls = editable.map((field) => {
    const key = field.id ?? field.name;
    const current = values.get(key) ?? '';
    const common = { label: field.name, required: field.requiredInStatus !== undefined };

    const control =
      field.type === 'number'
        ? numberField({ ...common, mono: true })
        : field.type === 'date'
          ? dateTimeField({ ...common, withTime: false })
          : textField({ ...common, maxLength: 1000 });

    if (current) control.input.value = current;
    control.input.addEventListener('input', () => {
      values.set(key, control.input.value);
      updateHeader();
      rememberDraft();
    });

    return { field, key, control };
  });

  updateHeader();

  host.replaceChildren(
    h(
      'div',
      { class: 'panel pad24 col', style: { gap: '18px' } },
      h(
        'div',
        { class: 'col', style: { gap: '2px' } },
        h('span', {
          class: 'h3',
          text: typeField?.value || task.projectTitle || t('task.customFields'),
        }),
        missing,
      ),
      h(
        'div',
        { class: 'grid c2', style: { gap: '16px 20px' } },
        ...controls.map((entry) => entry.control.element),
      ),
      h(
        'div',
        {
          class: 'row g10',
          style: {
            alignItems: 'flex-start',
            padding: '12px',
            borderRadius: '10px',
            background: 'var(--info-soft)',
          },
        },
        h('span', { class: 'ic', style: { color: 'var(--info)', flex: 'none' } }, icon(icons.info)),
        h('span', {
          class: 'body-sm',
          style: { color: 'var(--info)' },
          text:
            t('task.fieldsFromTemplate'),
        }),
      ),
    ),
    h(
      'div',
      { class: 'row g8', style: { alignItems: 'center' } },
      savedNote,
      h('button', {
        class: 'btn btn-secondary',
        type: 'button',
        style: { marginLeft: 'auto' },
        text: t('general.cancel'),
        onClick: () => {
          // A piszkozat eldobasa: a mezok visszaallnak a MENTETT ertekre.
          try {
            localStorage.removeItem(draftKey);
          } catch {
            // nincs teendo
          }
          for (const entry of controls) {
            entry.control.input.value = entry.field.value ?? '';
            values.set(entry.key, entry.field.value ?? '');
          }
          savedNote.style.display = 'none';
          updateHeader();
        },
      }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!validateAll(controls.map((entry) => entry.control) as FieldControl<HTMLElement>[])) {
            return;
          }
          try {
            localStorage.removeItem(draftKey);
          } catch {
            // nincs teendo
          }
          actions.onSaveFields(task, Object.fromEntries(values));
        },
      }),
    ),
  );

  return host;
}

/// Lejatszas es megallitas. Nem a kozos `icons` keszletbol: ez a ket jel
/// sehol masutt nem kell, es a keszletet nem hizlaljuk miattuk.
const ICON_PLAY = 'M5.5 3.5l7 4.5-7 4.5z';
const ICON_STOP = 'M4.5 4.5h7v7h-7z';

export function taskDetail(
  task: TaskView,
  people: PersonView[],
  actions: TaskActions,
): HTMLElement {
  const assignees = task.assigneeIds.map((id) => nameOf(people, id));
  const canAccept = task.status === 'waitingToAccept';
  const closed = task.status === 'completed' || task.status === 'failed';
  const typeField = task.fields.find((field) => field.isType);
  const headerFields = task.fields.filter((field) => !field.isType && field.onMainScreen);

  const body = h('div', { class: 'col', style: { gap: '0' } });

  const tabDefs: { key: DetailTab; label: string; count?: number }[] = [
    { key: 'timeline', label: t('task.timeline') },
    { key: 'comments', label: t('report.comments'), count: task.commentCount },
    { key: 'attachments', label: t('task.attachments'), count: task.attachmentCount },
    { key: 'fields', label: t('report.contentFields') },
  ];

  const tabStrip = h('div', { class: 'tabs', role: 'tablist' });

  function drawBody(): void {
    body.replaceChildren(
      activeTab === 'timeline'
        ? timelineTab(task, people)
        : activeTab === 'comments'
          ? commentsTab(task, people, actions)
          : activeTab === 'attachments'
            ? attachmentsTab(task, actions)
            : fieldsTab(task, actions),
    );
  }

  function drawTabs(): void {
    tabStrip.replaceChildren(
      ...tabDefs.map((def) =>
        h(
          'button',
          {
            class: `tab${def.key === activeTab ? ' on' : ''}`,
            type: 'button',
            role: 'tab',
            'aria-selected': def.key === activeTab ? 'true' : 'false',
            onClick: () => {
              activeTab = def.key;
              drawTabs();
              drawBody();
            },
          },
          def.label,
          def.count !== undefined
            ? h('span', { class: 'mono tm', text: String(def.count) })
            : null,
        ),
      ),
    );
  }

  drawTabs();
  drawBody();

  const menuItems: { label: string; onSelect: () => void }[] = [];
  if (canAccept) {
    menuItems.push({
      label: t('task.acceptOrDecline'),
      onSelect: () => actions.onAcceptDecline(task),
    });
  }
  if (closed) {
    menuItems.push({ label: t('general.reopen'), onSelect: () => actions.onReopen(task) });
  }
  menuItems.push(
    { label: t('task.timeTracking'), onSelect: () => actions.onOverrideTime(task) },
    { label: t('task.writeComment'), onSelect: () => actions.onComment(task) },
    { label: t('task.addAttachment'), onSelect: () => actions.onAttach(task) },
    { label: t('task.signatureLabel'), onSelect: () => actions.onSign(task) },
    { label: t('task.fillFields'), onSelect: () => actions.onEditFields(task) },
  );

  return h(
    'article',
    {
      class: 'col',
      style: {
        gap: '16px',
        height: '100%',
        overflow: 'auto',
        padding: '20px',
        background: 'var(--bg)',
      },
    },

    // --- fejléc: azonosító, cím, jelvények, műveletek ---
    h(
      'div',
      { class: 'row between g16', style: { alignItems: 'flex-start' } },
      h(
        'div',
        { class: 'col', style: { gap: '6px', minWidth: '0' } },
        h('span', {
          class: 'mono caption',
          text: [task.sequenceNumber ? `#${task.sequenceNumber}` : null, task.projectTitle]
            .filter(Boolean)
            .join(' · '),
        }),
        h('h2', { class: 'h2', text: task.title }),
        h(
          'div',
          { class: 'row g8 wrap' },
          statusBadge(task.status),
          task.priority ? priorityTag(task.priority) : null,
          // A terv a határidőt is jelvényként mutatja a cím alatt, nem csak a
          // listában — lejárva piros.
          task.deadline
            ? h(
                'span',
                { class: `badge ${task.isOverdue ? 'st-fail' : 'st-wait'}` },
                h('span', { class: 'ic' }, icon(icons.calendar, 12)),
                h('span', { text: `Határidő ${formatDeadlineShort(task.deadline)}` }),
              )
            : null,
        ),
      ),
      h(
        'div',
        { class: 'row g8', style: { flex: 'none' } },
        // Indítás / leállítás. A futó mérés MÁSKÉPP néz ki, és kiírja az
        // eddig eltelt időt — enélkül a gomb nem árulja el, hogy fut-e.
        closed
          ? null
          : h(
              'button',
              {
                class: task.timerRunning ? 'btn btn-danger' : 'btn btn-secondary',
                type: 'button',
                onClick: () =>
                  task.timerRunning ? actions.onStopTimer(task) : actions.onStartTimer(task),
              },
              h('span', { class: 'ic' }, icon(task.timerRunning ? ICON_STOP : ICON_PLAY, 15)),
              h('span', {
                text: task.timerRunning
                  ? t('task.stopTimer', [formatElapsed(task.elapsedSeconds)])
                  : t('task.startTimer'),
              }),
            ),
        closed
          ? h('button', {
              class: 'btn btn-primary',
              type: 'button',
              text: t('general.reopen'),
              onClick: () => actions.onReopen(task),
            })
          : h('button', {
              class: 'btn btn-primary',
              type: 'button',
              text: canAccept ? t('task.acceptOrDecline') : t('task.changeStatusShort'),
              onClick: () =>
                canAccept ? actions.onAcceptDecline(task) : actions.onChangeStatus(task),
            }),
        overflowMenu(menuItems),
        actions.onClose
          ? h('button', {
              class: 'btn btn-ghost btn-icon',
              type: 'button',
              'aria-label': t('task.closeDetails'),
              text: '✕',
              onClick: actions.onClose,
            })
          : null,
      ),
    ),

    // --- adatkártya: fekvő címke/érték sorok, a terv szerint ---
    h(
      'div',
      { class: 'card pad16 col', style: { gap: '2px' } },
      task.projectTitle ? detailRow('Projekt', task.projectTitle) : null,
      detailRow(
        t('task.assignees'),
        assignees.length === 0
          ? h('span', { class: 'body-sm tm', text: 'Nincs kiosztva' })
          : h(
              'span',
              { class: 'row g8', style: { alignItems: 'center', flexWrap: 'wrap' } },
              avatarStack(assignees, 3),
              h('span', { class: 'body-sm', text: assignees.join(', ') }),
            ),
      ),
      detailRow(
        'Kiosztotta',
        task.submittedUserId
          ? [nameOf(people, task.submittedUserId), stamp(task.createdAt)]
              .filter(Boolean)
              .join(' · ')
          : '—',
      ),
      task.followerUserId ? detailRow(t('task.follower'), nameOf(people, task.followerUserId)) : null,
      detailRow(
        t('report.colTracked'),
        h(
          'span',
          { class: 'row g6', style: { alignItems: 'center' } },
          h('span', { class: 'body-sm mono w6', text: formatElapsed(task.elapsedSeconds) }),
          task.timerRunning
            ? h('span', { class: 'badge st-prog', style: { height: '20px' }, text: 'fut' })
            : null,
        ),
      ),
      task.deadline ? detailRow(t('general.deadline'), formatDeadline(task.deadline)) : null,
      typeField ? detailRow(typeField.name, typeField.value ?? '—') : null,
      // A terv a „főképernyős" egyedi mezőket is ide teszi (Kazán típusa,
      // Garancia) — eddig sehol nem látszottak, csak a szerkesztőben.
      ...headerFields.map((field) =>
        detailRow(field.name, field.value ?? h('span', { class: 'body-sm tm', text: '—' })),
      ),
      detailRow(
        t('general.description'),
        task.description
          ? h('span', {
              class: 'body-sm',
              style: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
              text: task.description,
            })
          : h('span', { class: 'body-sm tm', text: t('task.noDescription') }),
      ),
    ),

    // --- fülek és tartalmuk ---
    tabStrip,
    body,
  );
}
